import { useEffect, useState } from "react";
import apiClient, { API } from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Download, Eye } from "lucide-react";

export function PRAssessmentsAdmin() {
  return <SubmissionList endpoint="/admin/pr-assessments" title="PR Assessments" testId="pr-assessments-table" type="pr" />;
}

export function StudentPRAssessmentsAdmin() {
  return <SubmissionList endpoint="/admin/student-pr-assessments" title="Student PR Assessments" testId="student-pr-assessments-table" type="student_pr" />;
}

export function ConsultationsAdmin() {
  return <SubmissionList endpoint="/admin/consultations" title="Consultations" testId="consultations-table" type="consultation" />;
}

export function MessagesAdmin() {
  return <SubmissionList endpoint="/admin/contact-messages" title="Contact Messages & Callback Requests" testId="messages-table" type="message" />;
}

export function SkilledMigrationAdmin() {
  return <SubmissionList endpoint="/admin/skilled-migration" title="Skilled Migration Assessments" testId="skilled-migration-table" type="skilled" />;
}

export function GSBuilderAdmin() {
  return <SubmissionList endpoint="/admin/gs-submissions" title="GS / SOP Builder Submissions" testId="gs-submissions-table" type="gs" />;
}

function SubmissionList({ endpoint, title, testId, type }) {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get(endpoint).then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, [endpoint]);

  const columns = {
    pr: [
      { key: "full_name", label: "Name" },
      { key: "email", label: "Email" },
      { key: "current_country", label: "Country" },
      { key: "preferred_visa_pathway", label: "Pathway" },
      { key: "created_at", label: "Submitted" },
    ],
    student_pr: [
      { key: "full_name", label: "Name" },
      { key: "email", label: "Email" },
      { key: "nationality", label: "Nationality" },
      { key: "current_course", label: "Course" },
      { key: "preferred_pr_pathway", label: "Pathway" },
      { key: "created_at", label: "Submitted" },
    ],
    consultation: [
      { key: "name", label: "Name" },
      { key: "email", label: "Email" },
      { key: "visa_type", label: "Visa" },
      { key: "preferred_date", label: "Date" },
      { key: "preferred_time", label: "Time" },
      { key: "consultation_type", label: "Type" },
      { key: "created_at", label: "Submitted" },
    ],
    message: [
      { key: "name", label: "Name" },
      { key: "email", label: "Email" },
      { key: "subject", label: "Subject" },
      { key: "created_at", label: "Submitted" },
    ],
    skilled: [
      { key: "profile.full_name", label: "Name" },
      { key: "profile.email", label: "Email" },
      { key: "profile.preferred_occupation", label: "Occupation" },
      { key: "points.total", label: "Points" },
      { key: "status", label: "Status" },
      { key: "created_at", label: "Submitted" },
    ],
    gs: [
      { key: "name", label: "Name" },
      { key: "email", label: "Email" },
      { key: "nationality", label: "Nationality" },
      { key: "status", label: "Status" },
      { key: "created_at", label: "Submitted" },
    ],
  }[type];

  return (
    <div className="p-8">
      <h1 className="font-display text-3xl text-[#29324A]">{title}</h1>
      <p className="text-sm text-slate-500 mt-1">{items.length} record{items.length === 1 ? "" : "s"}</p>

      <div className="mt-8 bg-white border border-slate-200 overflow-x-auto">
        {loading ? (
          <div className="p-10 text-center text-slate-500">Loading...</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-slate-500">No submissions yet.</div>
        ) : (
          <Table data-testid={testId}>
            <TableHeader>
              <TableRow>
                {columns.map((c) => (<TableHead key={c.key}>{c.label}</TableHead>))}
                <TableHead>Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  {columns.map((c) => {
                    const value = c.key.split(".").reduce((o, k) => (o == null ? o : o[k]), item);
                    return (
                      <TableCell key={c.key} className="text-sm">
                        {c.key === "created_at"
                          ? new Date(value).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })
                          : (value == null || value === "" ? "—" : value)}
                      </TableCell>
                    );
                  })}
                  <TableCell>
                    <button data-testid={`view-${item.id}`} onClick={() => setSelected(item)} className="text-[#29324A] hover:text-[#C43A89] inline-flex items-center gap-1 text-sm">
                      <Eye className="h-3.5 w-3.5" /> View
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{title} — Detail</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-2.5 text-sm">
              {Object.entries(selected).map(([k, v]) => {
                if (k === "_id" || k === "id" || k === "consent") return null;
                if (k === "cv_file_path" || k === "document_paths") return null;
                return (
                  <div key={k} className="grid grid-cols-3 py-2 border-b border-slate-100">
                    <div className="font-semibold text-[#29324A] capitalize">{k.replace(/_/g, " ")}</div>
                    <div className="col-span-2 text-slate-600 break-words">{Array.isArray(v) ? v.join(", ") : (v?.toString() || "—")}</div>
                  </div>
                );
              })}
              {selected.cv_filename && (
                <div className="pt-3"><span className="text-xs text-slate-500">CV: {selected.cv_filename}</span></div>
              )}
              {selected.document_filenames?.length > 0 && (
                <div className="pt-3"><span className="text-xs text-slate-500">Documents: {selected.document_filenames.join(", ")}</span></div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
